// ../analyzeSolanaTransaction/parseInstructionToTransfer.ts からコピー
// 現状の仕様を維持するために作成

import { z } from 'zod'
import type { TokenAccountMap } from '../analyzeSolanaTransaction/types'
import {
  addressLookupTableProgramId,
  bpfLoaderProgramId,
  stakeProgramId,
  systemProgramId,
  token2022ProgramId,
  tokenAccountRentExemptAmount,
  tokenProgramId,
  voteProgramId,
  wrappedSolTokenMintId,
} from '../constants'
import type { Instruction } from '../schemas/instruction'
import type { TransactionDetail } from '../schemas/transactionDetail'
import type { TokenTransfer } from '../schemas/transfer'
import type { SplTokenInfo } from '../splTokenDatasource'

// Helper function to create token info
const createTokenInfo = (
  mintAddress: string,
  splTokens: readonly SplTokenInfo[],
) => {
  const splTokenInfo = splTokens.find((token) => token.address === mintAddress)
  if (splTokenInfo && splTokenInfo.asset.type === 'CryptoCurrency') {
    return {
      address: mintAddress,
      name: splTokenInfo.asset.name,
      symbol: splTokenInfo.asset.symbol,
      decimals: splTokenInfo.decimals,
      currency: splTokenInfo.asset,
    }
  }
  return {
    address: mintAddress,
    name: '',
    symbol: '',
    decimals: 0,
    currency: {
      symbol: 'UNKNOWN',
      type: 'CryptoCurrency' as const,
      id: 'UNKNOWN',
      name: 'Unknown Token',
      updatedAt: process.env.NODE_ENV === 'test' ? 1672531200000 : Date.now(),
    },
  }
}

type ParseInstructionToTransferParameters = {
  readonly index: number
  readonly innerIndex: number | undefined
  readonly instruction: Instruction
  readonly transaction: TransactionDetail
  readonly tokenAccounts: TokenAccountMap
  readonly splTokens: readonly SplTokenInfo[]
}

export const parseInstructionToTransfer = ({
  index,
  innerIndex,
  instruction,
  transaction,
  tokenAccounts,
  splTokens,
}: ParseInstructionToTransferParameters): TokenTransfer | undefined => {
  // parse されていない場合は undefined を返す
  if (!('parsed' in instruction)) {
    return undefined
  }

  switch (instruction.programId) {
    case systemProgramId: {
      return parseSystemInstruction({
        index,
        innerIndex,
        instruction,
        transaction,
        tokenAccounts,
        splTokens,
      })
    }
    case stakeProgramId: {
      return parseStakeInstruction({
        index,
        innerIndex,
        instruction,
        transaction,
        tokenAccounts,
        splTokens,
      })
    }
    case voteProgramId: {
      switch (instruction.parsed.type) {
        case 'withdraw': {
          const voteWithdrawSchema = z.object({
            destination: z.string(),
            lamports: z.number().int().transform(BigInt),
            voteAccount: z.string(),
          })
          const { destination, lamports, voteAccount } =
            voteWithdrawSchema.parse(instruction.parsed.info)
          return {
            type: 'native',
            instructionIndex: index,
            innerInstructionIndex: innerIndex,
            from: voteAccount,
            to: destination,
            amount: lamports,
            programId: instruction.programId,
            instructionType: instruction.parsed.type,
          }
        }
        case 'towersync':
        case 'updateCommission': {
          // transaction fee のみ
          return undefined
        }
        default: {
          throw new Error('Not implemented')
        }
      }
    }
    case addressLookupTableProgramId: {
      console.debug('addressLookupTableProgramId:', instruction.parsed.type)
      return undefined
    }
    case bpfLoaderProgramId: {
      // TODO
      throw new Error('Not implemented')
    }
    case tokenProgramId:
    case token2022ProgramId: {
      switch (instruction.parsed.type) {
        case 'burn': {
          const burnSchema = z.object({
            account: z.string(),
            amount: z.string().regex(/^\d+$/).transform(BigInt),
            mint: z.string(),
          })
          const { account, amount, mint } = burnSchema.parse(
            instruction.parsed.info,
          )
          const fromOwner = tokenAccounts[account]?.owner
          return {
            type: 'spl',
            instructionIndex: index,
            innerInstructionIndex: innerIndex,
            token: createTokenInfo(mint, splTokens),
            from: fromOwner,
            to: undefined,
            amount,
            programId: instruction.programId,
            instructionType: instruction.parsed.type,
          }
        }
        case 'burnChecked': {
          const burnSchema = z.object({
            account: z.string(),
            tokenAmount: z.object({
              amount: z.string().regex(/^\d+$/).transform(BigInt),
            }),
            mint: z.string(),
          })
          const {
            account,
            tokenAmount: { amount },
            mint,
          } = burnSchema.parse(instruction.parsed.info)
          const fromOwner = tokenAccounts[account]?.owner
          return {
            type: 'spl',
            instructionIndex: index,
            innerInstructionIndex: innerIndex,
            token: createTokenInfo(mint, splTokens),
            from: fromOwner,
            to: undefined,
            amount,
            programId: instruction.programId,
            instructionType: instruction.parsed.type,
          }
        }
        case 'closeAccount': {
          const closeAccountSchema = z.object({
            account: z.string(),
            destination: z.string(),
          })
          const { account, destination } = closeAccountSchema.parse(
            instruction.parsed.info,
          )

          // Check if this is a WSOL token account
          const tokenAccountInfo = tokenAccounts[account]
          let isWsolAccount = tokenAccountInfo?.mint === wrappedSolTokenMintId

          // If not found in tokenAccounts, check if this account was initialized as WSOL in this transaction
          if (!isWsolAccount) {
            // Search for initializeAccount or initializeAccount3 instruction for this account with WSOL mint
            for (const instruction of [
              ...transaction.transaction.message.instructions,
              ...(transaction.meta?.innerInstructions?.flatMap(
                (innerInstruction) => innerInstruction.instructions,
              ) ?? []),
            ]) {
              if (
                'parsed' in instruction &&
                (instruction.parsed?.type === 'initializeAccount3' ||
                  instruction.parsed?.type === 'initializeAccount') &&
                instruction.parsed?.info?.account === account &&
                instruction.parsed?.info?.mint === wrappedSolTokenMintId
              ) {
                isWsolAccount = true
                break
              }
            }
          }

          let amount: bigint
          if (isWsolAccount) {
            // For WSOL token accounts, when closed, we need to calculate the remaining WSOL balance
            // Look for the account creation and all WSOL transfers to calculate the remaining balance

            // Find the initial SOL amount from createAccount instruction
            let initialAmount = BigInt(0)
            for (const inst of [
              ...transaction.transaction.message.instructions,
              ...(transaction.meta?.innerInstructions?.flatMap(
                (i) => i.instructions,
              ) ?? []),
            ]) {
              if (
                'parsed' in inst &&
                inst.parsed?.type === 'createAccount' &&
                inst.parsed?.info?.newAccount === account
              ) {
                initialAmount = BigInt(
                  inst.parsed.info.lamports as string | number,
                )
                break
              }
            }

            // If we found the initial amount, calculate the net WSOL after all transfers
            if (initialAmount > 0) {
              // Calculate all incoming and outgoing WSOL transfers for this account
              let totalIncoming = BigInt(0)
              let totalOutgoing = BigInt(0)
              for (const inst of [
                ...transaction.transaction.message.instructions,
                ...(transaction.meta?.innerInstructions?.flatMap(
                  (i) => i.instructions,
                ) ?? []),
              ]) {
                if (
                  'parsed' in inst &&
                  inst.parsed?.type === 'transfer' &&
                  inst.programId ===
                    'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA'
                ) {
                  if (inst.parsed?.info?.source === account) {
                    totalOutgoing += BigInt(inst.parsed.info.amount as string)
                  } else if (inst.parsed?.info?.destination === account) {
                    totalIncoming += BigInt(inst.parsed.info.amount as string)
                  }
                }
              }

              // The amount returned is: initial SOL + incoming transfers - outgoing transfers
              amount = initialAmount + totalIncoming - totalOutgoing
            } else {
              // Fallback: use the account's SOL balance changes
              const accountIndex =
                transaction.transaction.message.accountKeys.findIndex(
                  (key) => key.pubkey === account,
                )
              if (accountIndex >= 0) {
                const preLamports = BigInt(
                  transaction.meta?.preBalances?.[accountIndex] ?? 0,
                )
                const postLamports = BigInt(
                  transaction.meta?.postBalances?.[accountIndex] ?? 0,
                )
                amount =
                  preLamports > postLamports
                    ? preLamports - postLamports
                    : BigInt(tokenAccountRentExemptAmount)
              } else {
                amount = BigInt(tokenAccountRentExemptAmount)
              }
            }
          } else {
            // For non-WSOL accounts, calculate the remaining SOL
            amount = getSolRemain(transaction, account, tokenAccounts)
          }

          return {
            type: 'native',
            instructionIndex: index,
            innerInstructionIndex: innerIndex,
            from: account,
            to: destination,
            amount,
            programId: instruction.programId,
            instructionType: instruction.parsed.type,
          }
        }
        case 'mintTo': {
          const mintSchema = z.object({
            account: z.string(),
            amount: z.string().regex(/^\d+$/).transform(BigInt),
            mint: z.string(),
          })
          const { account, amount, mint } = mintSchema.parse(
            instruction.parsed.info,
          )
          const toOwner = tokenAccounts[account]?.owner
          return {
            type: 'spl',
            instructionIndex: index,
            innerInstructionIndex: innerIndex,
            token: createTokenInfo(mint, splTokens),
            from: undefined,
            to: toOwner,
            amount,
            programId: instruction.programId,
            instructionType: instruction.parsed.type,
          }
        }
        case 'mintToChecked': {
          const mintSchema = z.object({
            account: z.string(),
            tokenAmount: z.object({
              amount: z.string().regex(/^\d+$/).transform(BigInt),
            }),
            mint: z.string(),
          })
          const { account, tokenAmount, mint } = mintSchema.parse(
            instruction.parsed.info,
          )
          const toOwner = tokenAccounts[account]?.owner
          return {
            type: 'spl',
            instructionIndex: index,
            innerInstructionIndex: innerIndex,
            token: createTokenInfo(mint, splTokens),
            from: undefined,
            to: toOwner,
            amount: tokenAmount.amount,
            programId: instruction.programId,
            instructionType: instruction.parsed.type,
          }
        }
        case 'setAuthority': {
          const setAuthoritySchema = z.object({
            account: z.string().optional(),
            mint: z.string().optional(),
            authority: z.string().optional(),
            multisigAuthority: z.string().optional(),
            authorityType: z.string(),
            newAuthority: z.string().nullable(),
          })
          const {
            account,
            authority,
            multisigAuthority,
            authorityType,
            newAuthority,
          } = setAuthoritySchema.parse(instruction.parsed.info)
          const from = authority ?? multisigAuthority
          switch (authorityType) {
            case 'accountOwner': {
              if (account === undefined) {
                throw new Error('setAuthority.account is undefined')
              }
              const amount = BigInt(
                (transaction.meta?.preTokenBalances ?? []).find(
                  (balance) =>
                    transaction.transaction.message.accountKeys[
                      balance.accountIndex
                    ].pubkey === account,
                )?.uiTokenAmount.amount ?? '0',
              )

              const token = tokenAccounts[account]?.mint
              if (token === undefined) {
                throw new Error(`Token account not found: ${account}`)
              }
              return {
                type: 'spl',
                instructionIndex: index,
                innerInstructionIndex: innerIndex,
                token: createTokenInfo(token, splTokens),
                from,
                to: newAuthority ?? undefined,
                amount,
                programId: instruction.programId,
                instructionType: instruction.parsed.type,
              }
            }
            case 'mintTokens':
            case 'freezeAccount': {
              return undefined
            }
            default: {
              console.debug('Unknown authorityType:', authorityType)
              return undefined
            }
          }
        }
        case 'transfer': {
          const transferSchema = z.object({
            source: z.string(),
            destination: z.string(),
            amount: z.string().regex(/^\d+$/).transform(BigInt),
            authority: z.string().optional(),
          })
          const { source, destination, amount, authority } =
            transferSchema.parse(instruction.parsed.info)
          const token =
            tokenAccounts[source]?.mint ?? tokenAccounts[destination]?.mint
          if (token === undefined) {
            // tokenAccounts に存在しない(Transaction 内で create と close が行われている)可能性が高い
            return undefined
          }
          // Transaction 内で create と close されたものは tokenAccounts に存在しないため、authority から判定
          const fromOwner = tokenAccounts[source]?.owner ?? authority
          let toOwner = tokenAccounts[destination]?.owner

          // WSOL を扱う場合に、SOL の受取のみに利用される account が close されており情報が取れないのと、authority から判定することができないため、
          // Transaction 内の signer から判定する
          // 実際に SOLSCAN では、Funded by のところが mainSigner だと確認できる(違うパターンがあったら、修正必要)
          if (toOwner === undefined) {
            const mainSigner =
              transaction.transaction.message.accountKeys[0].pubkey
            const signers = transaction.transaction.message.accountKeys.filter(
              (key) => key.signer === true,
            )
            if (signers.some((key) => key.pubkey === destination)) {
              toOwner = mainSigner
            }
          }
          return {
            type: 'spl',
            instructionIndex: index,
            innerInstructionIndex: innerIndex,
            token: createTokenInfo(token, splTokens),
            from: fromOwner,
            to: toOwner,
            amount,
            programId: instruction.programId,
            instructionType: instruction.parsed.type,
          }
        }
        case 'transferChecked': {
          const transferSchema = z.object({
            source: z.string(),
            destination: z.string(),
            tokenAmount: z.object({
              amount: z.string().regex(/^\d+$/).transform(BigInt),
            }),
          })
          const { source, destination, tokenAmount } = transferSchema.parse(
            instruction.parsed.info,
          )
          const token =
            tokenAccounts[source]?.mint ?? tokenAccounts[destination]?.mint
          if (token === undefined) {
            // tokenAccounts に存在しない(Transaction 内で create と close が行われている)可能性が高い
            return undefined
          }
          const fromOwner = tokenAccounts[source]?.owner
          const toOwner = tokenAccounts[destination]?.owner
          return {
            type: 'spl',
            instructionIndex: index,
            innerInstructionIndex: innerIndex,
            token: createTokenInfo(token, splTokens),
            from: fromOwner,
            to: toOwner,
            amount: tokenAmount.amount,
            programId: instruction.programId,
            instructionType: instruction.parsed.type,
          }
        }
        case 'initializeAccount': {
          return undefined
        }
        default: {
          console.debug(
            'Unknown token instruction type:',
            instruction.parsed.type,
          )
          return undefined
        }
      }
    }
    default: {
      return undefined
    }
  }
}

const parseSystemInstruction = ({
  index,
  innerIndex,
  instruction,
}: ParseInstructionToTransferParameters): TokenTransfer | undefined => {
  if (!('parsed' in instruction)) {
    return undefined
  }

  switch (instruction.parsed.type) {
    case 'createAccount':
    case 'createAccountWithSeed': {
      const createAccountSchema = z.object({
        lamports: z.number().int().transform(BigInt),
        newAccount: z.string(),
        source: z.string(),
      })
      const { lamports, newAccount, source } = createAccountSchema.parse(
        instruction.parsed.info,
      )
      return {
        type: 'native',
        instructionIndex: index,
        innerInstructionIndex: innerIndex,
        from: source,
        to: newAccount,
        amount: lamports,
        programId: instruction.programId,
        instructionType: instruction.parsed.type,
      }
    }
    case 'transfer':
    case 'transferWithSeed': {
      const transferSchema = z.object({
        lamports: z.number().int().transform(BigInt),
        source: z.string(),
        destination: z.string(),
      })
      const { lamports, source, destination } = transferSchema.parse(
        instruction.parsed.info,
      )
      return {
        type: 'native',
        instructionIndex: index,
        innerInstructionIndex: innerIndex,
        from: source,
        to: destination,
        amount: lamports,
        programId: instruction.programId,
        instructionType: instruction.parsed.type,
      }
    }
    case 'withdrawNonceAccount': {
      // nonce から to への lamports 分の transfer だと思われる
      // TODO
      throw new Error('Not implemented')
    }
    case 'allocate':
    case 'assign': {
      return undefined
    }
    default: {
      console.debug('Unknown system instruction type:', instruction.parsed.type)
      return undefined
    }
  }
}

const parseStakeInstruction = ({
  index,
  innerIndex,
  instruction,
}: ParseInstructionToTransferParameters): TokenTransfer | undefined => {
  if (!('parsed' in instruction)) {
    return undefined
  }

  switch (instruction.parsed.type) {
    case 'initialize':
    case 'initializeChecked':
    case 'authorize':
    case 'authorizeWithSeed':
    case 'authorizeChecked':
    case 'authorizeCheckedWithSeed': {
      return undefined
    }
    case 'delegateStake': {
      throw new Error('Not implemented')
    }
    case 'split': {
      const splitSchema = z.object({
        // "lamports": 119889354,
        lamports: z.number().int().transform(BigInt),
        // "newSplitAccount": "89JRFCBDT3kDZFDi5Vh6hskfhxA44Cmgmgjj1TokPBVS",
        newSplitAccount: z.string(),
        // "stakeAccount": "HD63esu4hWwuVwiHBLKAtukLCJLogrsd97mGYWUG1etn",
        stakeAccount: z.string(),
        // "stakeAuthority": "2oFvntttTFoEejX5uRoP5NLovNjhe7fiCZhs3Td1tpYa"
        stakeAuthority: z.string(),
      })
      const { lamports, newSplitAccount, stakeAccount } = splitSchema.parse(
        instruction.parsed.info,
      )
      return {
        type: 'native',
        instructionIndex: index,
        innerInstructionIndex: innerIndex,
        from: stakeAccount,
        to: newSplitAccount,
        amount: lamports,
        programId: instruction.programId,
        instructionType: instruction.parsed.type,
      }
    }
    case 'withdraw': {
      const withdrawSchema = z.object({
        // "destination": "BgKUXdS29YcHCFrPm5M8oLHiTzZaMDjsebggjoaQ6KFL",
        destination: z.string(),
        // "lamports": 2282880,
        lamports: z.number().int().transform(BigInt),
        // "stakeAccount": "D4n39Kk3FCXWFUhjosBW5tmHMuBX2WoD8asWwjk85puv",
        stakeAccount: z.string(),
      })
      const { destination, lamports, stakeAccount } = withdrawSchema.parse(
        instruction.parsed.info,
      )
      return {
        type: 'native',
        instructionIndex: index,
        innerInstructionIndex: innerIndex,
        from: stakeAccount,
        to: destination,
        amount: lamports,
        programId: instruction.programId,
        instructionType: instruction.parsed.type,
      }
    }
    case 'deactivate': {
      return undefined
    }
    case 'setLockup': {
      throw new Error('Not implemented')
    }
    case 'merge': {
      // merge は merge amount が不明なので処理できない
      return undefined
    }
    case 'setLockupChecked': {
      throw new Error('Not implemented')
    }
    case 'getMinimumDelegation': {
      return undefined
    }
    case 'deactivateDelinquent': {
      return undefined
    }
    case 'redelegate': {
      return undefined
    }
    case 'moveStake': {
      throw new Error('Not implemented')
    }
    case 'moveLamports': {
      throw new Error('Not implemented')
    }
    default: {
      console.debug('Unknown system instruction type:', instruction.parsed.type)
      return undefined
    }
  }
}

const getSolRemain = (
  transaction: TransactionDetail,
  account: string,
  tokenAccounts: TokenAccountMap,
): bigint => {
  const accountIndex = transaction.transaction.message.accountKeys.findIndex(
    (key) => key.pubkey === account,
  )
  if (accountIndex === -1) {
    throw new Error(`${account}'s accountKeys not found`)
  }
  const preBalance = transaction.meta?.preBalances[accountIndex]
  const postBalance = transaction.meta?.postBalances[accountIndex]
  if (preBalance === undefined || postBalance === undefined) {
    throw new Error('preBalance or postBalance is undefined')
  }
  let mut_remain = BigInt(preBalance) - BigInt(postBalance)
  for (const instruction of [
    ...transaction.transaction.message.instructions,
    ...(transaction.meta?.innerInstructions?.flatMap(
      (innerInstruction) => innerInstruction.instructions,
    ) ?? []),
  ]) {
    if (!('parsed' in instruction)) {
      continue
    }
    switch (instruction.programId) {
      case systemProgramId: {
        const transfer = parseSystemInstruction({
          index: 0,
          innerIndex: undefined,
          instruction,
          transaction,
          tokenAccounts,
          splTokens: [],
        })
        if (transfer === undefined) {
          continue
        }
        if (transfer.from === account) {
          mut_remain -= transfer.amount
        }
        if (transfer.to === account) {
          mut_remain += transfer.amount
        }
        break
      }
      case tokenProgramId:
      case token2022ProgramId: {
        switch (instruction.parsed.type) {
          case 'transfer': {
            const transferSchema = z.object({
              source: z.string(),
              destination: z.string(),
              amount: z.string().regex(/^\d+$/).transform(BigInt),
            })
            const { source, destination, amount } = transferSchema.parse(
              instruction.parsed.info,
            )
            const tokenAccountInfo = tokenAccounts[source]
            if (
              tokenAccountInfo === undefined ||
              tokenAccountInfo.mint !== wrappedSolTokenMintId
            ) {
              continue
            }
            if (source === account) {
              mut_remain -= amount
            }
            if (destination === account) {
              mut_remain += amount
            }
            break
          }
          case 'transferChecked': {
            const transferSchema = z.object({
              source: z.string(),
              destination: z.string(),
              tokenAmount: z.object({
                amount: z.string().regex(/^\d+$/).transform(BigInt),
              }),
            })
            const {
              source,
              destination,
              tokenAmount: { amount },
            } = transferSchema.parse(instruction.parsed.info)
            const tokenAccountInfo = tokenAccounts[source]
            if (
              tokenAccountInfo === undefined ||
              tokenAccountInfo.mint !== wrappedSolTokenMintId
            ) {
              continue
            }
            if (source === account) {
              mut_remain -= amount
            }
            if (destination === account) {
              mut_remain += amount
            }
            break
          }
          default: {
            continue
          }
        }
        break
      }
      default: {
        continue
      }
    }
  }
  return mut_remain
}
